import React, { useEffect, useState } from 'react';
import podService from '../services/podService';
import ConfirmModal from './ConfirmModal';

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function fmtGps(lat, lng) {
  if (lat == null || lng == null) return null;
  return `${Number(lat).toFixed(5)}°, ${Number(lng).toFixed(5)}°`;
}

function PhotoRecord({ pod, onDelete }) {
  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <span className="badge bg-secondary me-2">📎 Photo</span>
            <span className="text-muted" style={{ fontSize: 12 }}>{fmtDateTime(pod.capturedAt)}</span>
          </div>
          <button className="btn btn-outline-danger btn-sm" onClick={() => onDelete(pod)}>Delete</button>
        </div>
        {pod.photoSignedUrl && (
          <div className="mb-2">
            <a href={pod.photoSignedUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={pod.photoSignedUrl}
                alt="Proof of delivery"
                style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 6, border: '1px solid #dee2e6', cursor: 'zoom-in' }}
              />
            </a>
          </div>
        )}
        {pod.receiverNotes && (
          <div className="text-muted" style={{ fontSize: 13 }}>
            <strong>Notes:</strong> {pod.receiverNotes}
          </div>
        )}
        {pod.photoSignedUrl && (
          <a href={pod.photoSignedUrl} download className="btn btn-outline-secondary btn-sm mt-2">
            ⬇ Download Photo
          </a>
        )}
      </div>
    </div>
  );
}

function SignatureRecord({ pod, onDelete }) {
  const gps = fmtGps(pod.latitude, pod.longitude);

  return (
    <div className="card mb-3" style={{ borderColor: '#198754' }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <span className="badge bg-success me-2">✅ Signed</span>
            <span className="text-muted" style={{ fontSize: 12 }}>{fmtDateTime(pod.capturedAt)}</span>
          </div>
          <button className="btn btn-outline-danger btn-sm" onClick={() => onDelete(pod)}>Delete</button>
        </div>

        <div className="mb-2" style={{ fontSize: 13 }}>
          <strong>Signed by:</strong> {pod.signedByName || '—'}
        </div>
        {gps && (
          <div className="mb-2" style={{ fontSize: 12, color: '#6c757d' }}>
            📍 {gps}
          </div>
        )}
        {pod.signatureSignedUrl && (
          <div className="mb-2">
            <div className="text-muted mb-1" style={{ fontSize: 12 }}>Signature:</div>
            <img
              src={pod.signatureSignedUrl}
              alt="Signature"
              style={{ maxWidth: 280, maxHeight: 100, border: '1px solid #dee2e6', borderRadius: 4, background: '#fff', padding: 4 }}
            />
          </div>
        )}
        {pod.receiverNotes && (
          <div className="text-muted mb-2" style={{ fontSize: 13 }}>
            <strong>Notes:</strong> {pod.receiverNotes}
          </div>
        )}
        <div className="d-flex gap-2 mt-2 flex-wrap">
          {pod.pdfSignedUrl && (
            <a href={pod.pdfSignedUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
              📄 View PDF Receipt
            </a>
          )}
          {pod.pdfSignedUrl && (
            <a href={pod.pdfSignedUrl} download className="btn btn-outline-secondary btn-sm">
              ⬇ Download PDF
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PodViewModal({ orderId, orderNumber, onClose, onDeleted }) {
  const [pods,    setPods]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    podService.getOrderPods(orderId)
      .then(setPods)
      .catch(() => setError('Failed to load proof of delivery records.'))
      .finally(() => setLoading(false));
  }, [orderId]);

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await podService.deletePod(toDelete.id);
      setPods(prev => prev.filter(p => p.id !== toDelete.id));
      setToDelete(null);
      if (onDeleted) onDeleted(toDelete.id);
    } catch {
      setError('Failed to delete record.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Proof of Delivery — Order #{orderNumber}</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {loading && <div className="text-center py-4"><span className="spinner-border spinner-border-sm" /></div>}
              {error && <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div>}
              {!loading && pods.length === 0 && (
                <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>
                  No proof of delivery records for this order.
                </div>
              )}
              {pods.map(pod =>
                pod.captureMethod === 'signature'
                  ? <SignatureRecord key={pod.id} pod={pod} onDelete={setToDelete} />
                  : <PhotoRecord     key={pod.id} pod={pod} onDelete={setToDelete} />
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        show={!!toDelete}
        message={toDelete ? `Delete this ${toDelete.captureMethod === 'signature' ? 'signature' : 'photo'} proof of delivery record? This cannot be undone.` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
      />
    </>
  );
}
